import { ReactNode } from 'react';

import { useNavigate } from 'react-router-dom';

import { BreadcrumbItem } from './breadcrumb';
import { Icon } from './icon';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './shadcn/dropdown-menu';
import { Permission } from '../../../../backend/src/auth/permissions/permission';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useGetRecentMatters } from '@/api/queries/useGetRecentMatters';
import { useGetRecentSubsets } from '@/api/queries/useGetRecentSubsets';
import { PermissionGate } from '@/hooks/useHasPermission';
import { Skeleton } from '@/new/components/shadcn/skeleton';
import { cn } from '@/new/lib/utils';

const MAX_RECENT_MATTERS = 5;

interface BreadcrumbDropdownItem {
  key: string;
  name: string;
  active: boolean;
  onSelect: () => void;
}

interface BreadcrumbDropdownProps {
  /** Iconify class shown before the label in the trigger. */
  iconClass: string;
  /** Label shown in the trigger (the current matter/subset name). */
  label: string;
  /** Whether this is the active (last) breadcrumb item. */
  active: boolean;
  /** Heading above the list of recent items. */
  menuLabel: string;
  /** Recently-accessed items to switch between. */
  items: BreadcrumbDropdownItem[];
  /** Whether the recent items are still loading. */
  isLoading?: boolean;
  /** Label for the "view all" footer action. */
  viewAllLabel: string;
  onViewAll: () => void;
  /** Optional extra footer actions (e.g. "Create subset"). */
  children?: ReactNode;
}

/**
 * Shared breadcrumb switcher: a dropdown trigger styled to sit inline in the breadcrumb,
 * listing recent items with the current one highlighted, plus footer actions.
 */
function BreadcrumbDropdown({
  iconClass,
  label,
  active,
  menuLabel,
  items,
  isLoading = false,
  viewAllLabel,
  onViewAll,
  children,
}: BreadcrumbDropdownProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          'inline-flex items-center gap-2 duration-150 outline-none hover:text-gray-800 data-[state=open]:text-gray-800',
          active && 'text-gray-800',
        )}
      >
        <Icon className={iconClass} />
        <span className="line-clamp-1 max-w-52 break-all">{label}</span>
        <Icon className="lucide--chevron-down text-sm text-gray-400" />
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" alignOffset={-6} className="min-w-56">
        <DropdownMenuLabel>{menuLabel}</DropdownMenuLabel>

        {isLoading &&
          Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="flex h-6 items-center px-2">
              <Skeleton className="h-4 w-40" />
            </div>
          ))}

        {items.map((item) => (
          <DropdownMenuItem
            key={item.key}
            onSelect={item.onSelect}
            className={cn(item.active ? 'text-gray-900' : 'text-gray-500')}
          >
            <span className="line-clamp-1 break-all">{item.name}</span>
            {item.active && <Icon className="lucide--check ml-auto pl-2 text-gray-500" />}
          </DropdownMenuItem>
        ))}

        <DropdownMenuSeparator />

        <DropdownMenuItem onSelect={onViewAll}>
          <Icon className="lucide--list" />
          <span>{viewAllLabel}</span>
        </DropdownMenuItem>

        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface MatterBreadcrumbDropdownProps {
  matterId: string;
  /** The current matter name. */
  matterName: string;
  active: boolean;
}

/**
 * Builds the leading "Matters / <matter>" items of a breadcrumb trail, falling back to the
 * recent-matters cache for the name and emitting a loading marker until it resolves.
 */
export function useMatterBreadcrumbHead(
  matterId: string,
  matterName: string | undefined,
  active: boolean,
): BreadcrumbItem[] {
  const { data: recentMattersData } = useGetRecentMatters();
  const resolved = matterName ?? recentMattersData?.find((matter) => matter.id === matterId)?.name;

  return [
    { href: '/matters', label: 'Matters', active: false },
    resolved !== undefined
      ? {
          key: 'matter',
          render: <MatterBreadcrumbDropdown matterId={matterId} matterName={resolved} active={active} />,
        }
      : { key: 'matter', loading: true },
  ];
}

export function MatterBreadcrumbDropdown({ matterId, matterName, active }: MatterBreadcrumbDropdownProps) {
  const navigate = useNavigate();
  const { data: recentMattersData, isLoading } = useGetRecentMatters();

  // Take the dedicated recent matters, capped, ensuring the current matter is always present.
  const recentMatters: { id: string; name: string }[] = [];
  const seen = new Set<string>();
  for (const matter of recentMattersData ?? []) {
    if (seen.has(matter.id)) {
      continue;
    }
    seen.add(matter.id);
    recentMatters.push({ id: matter.id, name: matter.name });
    if (recentMatters.length >= MAX_RECENT_MATTERS) {
      break;
    }
  }

  // Ensure the current matter is always present and marked.
  if (!seen.has(matterId)) {
    recentMatters.unshift({ id: matterId, name: matterName });
  }

  return (
    <BreadcrumbDropdown
      iconClass="lucide--briefcase"
      label={matterName}
      active={active}
      menuLabel="Recent Matters"
      items={recentMatters.map((matter) => ({
        key: matter.id,
        name: matter.name,
        active: matter.id === matterId,
        onSelect: () => navigate(`/matters/${matter.id}`),
      }))}
      isLoading={isLoading}
      viewAllLabel="View all Matters"
      onViewAll={() => navigate('/matters')}
    />
  );
}

interface SubsetBreadcrumbDropdownProps {
  matterId: string;
  subsetId: string;
  /** The current subset name. */
  subsetName: string;
  active: boolean;
}

export function SubsetBreadcrumbDropdown({ matterId, subsetId, subsetName, active }: SubsetBreadcrumbDropdownProps) {
  const navigate = useNavigate();
  const { data: recentSubsets, isLoading } = useGetRecentSubsets(matterId);

  return (
    <BreadcrumbDropdown
      iconClass="lucide--group"
      label={subsetName}
      active={active}
      menuLabel="Recent Subsets"
      items={(recentSubsets ?? []).map((subset) => ({
        key: subset.caseId,
        name: subset.case.name,
        active: subset.caseId === subsetId,
        onSelect: () => navigate(`/matters/${matterId}/subsets/${subset.caseId}`),
      }))}
      isLoading={isLoading}
      viewAllLabel="View all Subsets"
      onViewAll={() => navigate(`/matters/${matterId}/subsets`)}
    >
      <PermissionGate permission={Permission.SubsetCreate}>
        <DropdownMenuItem
          onSelect={() => {
            trackCustomEvent(TrackingEventsV2.SubsetOpenCreateNew, { matterId });
            navigate(`/matters/${matterId}/subsets/new`);
          }}
        >
          <Icon className="lucide--plus" />
          <span>Create Subset</span>
        </DropdownMenuItem>
      </PermissionGate>
    </BreadcrumbDropdown>
  );
}
